// ======================================================================
// FILE:
// src/modules/service_crm/tickets/pages/ServiceTicketListPage.js
//
// MODULE: Service CRM
// FEATURE: Service Ticket List
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import serviceTicketService
from "../../services/serviceTicketService";

export default function ServiceTicketListPage() {

  const navigate = useNavigate();

  // -------------------------------------------------------------------
  // STATE
  // -------------------------------------------------------------------
  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [pagination, setPagination] =
    useState({});

  // -------------------------------------------------------------------
  // FILTERS
  // -------------------------------------------------------------------
  const [filters, setFilters] =
    useState({

      q: "",

      status: "",

      priority: "",

      page: 1,

      per_page: 10,
    });

  // -------------------------------------------------------------------
  // LOAD
  // -------------------------------------------------------------------
  useEffect(() => {

    loadData();

  }, [filters]);

  // -------------------------------------------------------------------
  // LOAD DATA
  // -------------------------------------------------------------------
  async function loadData() {

    try {

      setLoading(true);

      const res =
        await serviceTicketService
          .listTickets(filters);

      setRows(
        res.data || []
      );

      setPagination(
        res.pagination || {}
      );

    } catch (err) {

      console.error(err);

      alert(
        "Failed to load tickets"
      );

    } finally {

      setLoading(false);
    }
  }

  // -------------------------------------------------------------------
  // FILTER CHANGE
  // -------------------------------------------------------------------
  function handleFilterChange(
    e
  ) {

    setFilters({

      ...filters,

      [e.target.name]:
        e.target.value,

      page: 1,
    });
  }

  // -------------------------------------------------------------------
  // STATUS BADGE
  // -------------------------------------------------------------------
  function renderStatus(status) {

    const value =
      (status || "").toUpperCase();

    let bg = "#6c757d";

    if (value === "OPEN") {
      bg = "#dc3545";
    }

    else if (
      value === "ASSIGNED"
    ) {
      bg = "#fd7e14";
    }

    else if (
      value === "IN_PROGRESS"
    ) {
      bg = "#0d6efd";
    }

    else if (
      value === "RESOLVED"
    ) {
      bg = "#198754";
    }

    else if (
      value === "CLOSED"
    ) {
      bg = "#212529";
    }

    return (

      <span
        style={{

          background: bg,

          color: "#fff",

          padding:
            "5px 10px",

          borderRadius: 20,

          fontSize: 12,

          fontWeight: 600,
        }}
      >
        {status}
      </span>
    );
  }

  // -------------------------------------------------------------------
  // KPI COUNTS
  // -------------------------------------------------------------------
  const openCount =
    rows.filter(
      r => r.status === "OPEN"
    ).length;

  const resolvedCount =
    rows.filter(
      r => r.status === "RESOLVED"
    ).length;

  const closedCount =
    rows.filter(
      r => r.status === "CLOSED"
    ).length;

  // -------------------------------------------------------------------
  // RENDER
  // -------------------------------------------------------------------
  return (

    <div>

      {/* ========================================================= */}
      {/* PAGE HEADER */}
      {/* ========================================================= */}

      <div
        style={{

          display: "flex",

          justifyContent:
            "space-between",

          alignItems: "center",

          marginBottom: 20,

          flexWrap: "wrap",

          gap: 12,
        }}
      >

        <div>

          <h2
            style={{
              margin: 0,
            }}
          >
            Service Tickets
          </h2>

          <div
            style={{
              color: "#6c757d",
            }}
          >
            Manage service requests
          </div>

        </div>

        <button
          className="btn btn-primary"

          onClick={() =>
            navigate(
              "/service-crm/tickets/create"
            )
          }
        >
          + Create Ticket
        </button>

      </div>

      {/* ========================================================= */}
      {/* KPI CARDS */}
      {/* ========================================================= */}

      <div
        style={{

          display: "grid",

          gridTemplateColumns:
            "repeat(auto-fit, minmax(220px, 1fr))",

          gap: 16,

          marginBottom: 20,
        }}
      >

        <KPI
          title="Open Tickets"
          value={openCount}
        />

        <KPI
          title="Resolved"
          value={resolvedCount}
        />

        <KPI
          title="Closed"
          value={closedCount}
        />

        <KPI
          title="Total"
          value={rows.length}
        />

      </div>

      {/* ========================================================= */}
      {/* FILTERS */}
      {/* ========================================================= */}

      <div
        style={{

          background: "#fff",

          borderRadius: 14,

          padding: 16,

          marginBottom: 20,

          boxShadow:
            "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >

        <div
          style={{

            display: "grid",

            gridTemplateColumns:
              "repeat(auto-fit, minmax(220px, 1fr))",

            gap: 14,
          }}
        >

          {/* SEARCH */}
          <input
            type="text"

            className="form-control"

            placeholder={
              "Search Ticket / Instance / Serial"
            }

            name="q"

            value={filters.q}

            onChange={
              handleFilterChange
            }
          />

          {/* STATUS */}
          <select
            className="form-select"

            name="status"

            value={filters.status}

            onChange={
              handleFilterChange
            }
          >

            <option value="">
              All Status
            </option>

            <option value="OPEN">
              OPEN
            </option>

            <option value="ASSIGNED">
              ASSIGNED
            </option>

            <option value="IN_PROGRESS">
              IN PROGRESS
            </option>

            <option value="RESOLVED">
              RESOLVED
            </option>

            <option value="CLOSED">
              CLOSED
            </option>

          </select>

          {/* PRIORITY */}
          <select
            className="form-select"

            name="priority"

            value={filters.priority}

            onChange={
              handleFilterChange
            }
          >

            <option value="">
              All Priority
            </option>

            <option value="Low">
              Low
            </option>

            <option value="Medium">
              Medium
            </option>

            <option value="High">
              High
            </option>

          </select>

        </div>

      </div>

      {/* ========================================================= */}
      {/* TABLE */}
      {/* ========================================================= */}

      <div
        style={{

          background: "#fff",

          borderRadius: 14,

          overflow: "hidden",

          boxShadow:
            "0 2px 8px rgba(0,0,0,0.06)",
        }}
      >

        <div
          style={{
            overflowX: "auto",
          }}
        >

          <table
            className="table table-hover"
            style={{
              marginBottom: 0,
            }}
          >

            <thead
              className="table-light"
            >

              <tr>

                <th>
                  Ticket No
                </th>

                <th>
                  Instance ID
                </th>

                <th>
                  Customer
                </th>

                <th>
                  Category
                </th>

                <th>
                  Priority
                </th>

                <th>
                  Engineer
                </th>

                <th>
                  Status
                </th>

                <th>
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>

                  <td
                    colSpan="8"
                    className="text-center py-4"
                  >
                    Loading...
                  </td>

                </tr>
              )}

              {!loading &&
                rows.length === 0 && (

                <tr>

                  <td
                    colSpan="8"
                    className="text-center py-4"
                  >
                    No tickets found
                  </td>

                </tr>
              )}

              {!loading &&
                rows.map((row) => (

                <tr key={row.id}>

                  <td>
                    {row.ticket_no}
                  </td>

                  <td>
                    {row.instance_id}
                  </td>

                  <td>
                    {row.customer_name}
                  </td>

                  <td>
                    {row.category || "-"}
                  </td>

                  <td>
                    {row.priority}
                  </td>

                  <td>
                    {row.assigned_engineer || "-"}
                  </td>

                  <td>
                    {renderStatus(
                      row.status
                    )}
                  </td>

                  <td>

                    <div
                      style={{

                        display: "flex",

                        gap: 8,

                        flexWrap: "wrap",
                      }}
                    >

                      <button
                        className="btn btn-sm btn-outline-primary"

                        onClick={() =>
                          navigate(
                            `/service-crm/tickets/${row.id}`
                          )
                        }
                      >
                        View
                      </button>

                      <button
                        className="btn btn-sm btn-outline-secondary"

                        onClick={() =>
                          navigate(
                            `/service-crm/tickets/${row.id}/edit`
                          )
                        }
                      >
                        Edit
                      </button>

                    </div>

                  </td>

                </tr>
              ))}

            </tbody>

          </table>

        </div>

      </div>

      {/* ========================================================= */}
      {/* PAGINATION */}
      {/* ========================================================= */}

      <div
        style={{

          display: "flex",

          justifyContent:
            "space-between",

          alignItems: "center",

          marginTop: 16,
        }}
      >

        <div>

          Total:
          {" "}
          {pagination.total || 0}

        </div>

        <div
          style={{

            display: "flex",

            gap: 8,
          }}
        >

          <button
            className="btn btn-outline-secondary btn-sm"

            disabled={
              filters.page <= 1
            }

            onClick={() =>
              setFilters({

                ...filters,

                page:
                  filters.page - 1,
              })
            }
          >
            Previous
          </button>

          <button
            className="btn btn-outline-secondary btn-sm"

            disabled={
              filters.page >=
              (pagination.pages || 1)
            }

            onClick={() =>
              setFilters({

                ...filters,

                page:
                  filters.page + 1,
              })
            }
          >
            Next
          </button>

        </div>

      </div>

    </div>
  );
}


// ======================================================================
// KPI CARD
// ======================================================================
function KPI({
  title,
  value,
}) {

  return (

    <div
      style={{

        background: "#fff",

        borderRadius: 14,

        padding: 18,

        boxShadow:
          "0 2px 8px rgba(0,0,0,0.06)",
      }}
    >

      <div
        style={{

          fontSize: 13,

          color: "#6c757d",

          marginBottom: 8,
        }}
      >
        {title}
      </div>

      <div
        style={{

          fontSize: 28,

          fontWeight: 700,
        }}
      >
        {value}
      </div>

    </div>
  );
}